import {
  collection,
  getDocs,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { motion } from "framer-motion";
import {
  AlertCircle,
  ArrowLeft,
  BarChart2,
  Clock,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../lib/firebase";
import { LoadingSpinner } from "../../components/LoadingSpinner";

export function Analytics() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalAssessments: 0,
    completionRate: 0,
    averageTime: 0,
    activeUsers: 0,
    monthlyGrowth: 0,
    dailyAssessments: [],
  });

  useEffect(() => {
    if (user?.uid) {
      loadAnalytics();
    }
  }, [user]);

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);

      // Get total assessments
      const assessmentsRef = collection(db, "respondents");
      const totalSnapshot = await getDocs(assessmentsRef);
      const totalCount = totalSnapshot.size;

      // Get completed assessments
      const completedQuery = query(assessmentsRef, where("results", "!=", {}));
      const completedSnapshot = await getDocs(completedQuery);
      const completedCount = completedSnapshot.size;

      // Calculate completion rate
      const completionRate = totalCount
        ? (completedCount / totalCount) * 100
        : 0;

      // Get active users (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const activeQuery = query(
        assessmentsRef,
        where("createdAt", ">=", Timestamp.fromDate(thirtyDaysAgo))
      );
      const activeSnapshot = await getDocs(activeQuery);
      const activeCount = activeSnapshot.size;

      setStats({
        totalAssessments: totalCount,
        completionRate: Math.round(completionRate),
        averageTime: 25, // Placeholder - would need actual timing data
        activeUsers: activeCount,
        monthlyGrowth: 15, // Placeholder - would need historical data
        dailyAssessments: [], // Placeholder - would need daily aggregation
      });
    } catch (err) {
      console.error("Error loading analytics:", err);
      setError("Failed to load analytics data");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  return (
    <div className="min-h-screen minimal-bg py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <BarChart2 className="w-12 h-12 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <p className="text-gray-600">Track system performance and usage</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 text-red-400 p-4 rounded-xl mb-6">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-6 bg-blue-50 border border-blue-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.totalAssessments}
                </div>
                <div className="text-sm text-gray-600">Total Assessments</div>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl p-6 bg-emerald-50 border border-emerald-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.completionRate}%
                </div>
                <div className="text-sm text-gray-600">Completion Rate</div>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl p-6 bg-purple-50 border border-purple-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.averageTime}m
                </div>
                <div className="text-sm text-gray-600">Average Time</div>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-2xl p-6 bg-amber-50 border border-amber-100 shadow-sm hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {stats.activeUsers}
                </div>
                <div className="text-sm text-gray-600">Active Users (30d)</div>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Growth Trends */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Growth Trends
            </h2>
            <div className="text-gray-500 text-center py-8">
              Growth visualization coming soon...
            </div>
          </motion.div>

          {/* Usage Patterns */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Usage Patterns
            </h2>
            <div className="text-gray-500 text-center py-8">
              Usage pattern visualization coming soon...
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}